import React, { useState, useEffect } from 'react';
import {
    Violator,
    GalleryNext,
    GalleryNextSlides,
    GalleryNextThumbnailsUS,
    GalleryNextBadges,
    GalleryNextExpandBtn,
    useGalleryNextContext,
    ImageWithPlaceholder,
} from '@hpi-sox/hpstellar-core';
import { Gallery } from '@hpi-sox/hpstellar-core/legacy';

import loadable from '@loadable/component';
import { Helpers } from '../../../../core/src/helpers';

import withConfigurations from '../../../../product/components/product-configurations';
import useTemplateTheme from '../../../../hooks/useTemplateTheme';
import {
    getEnvironmentalCertificationBadges,
    getConfigurations,
    getSelectedVariant,
    getColorId,
} from '../../../../product/get-product-props';
import withPageComponents from '../../../../page/components/with-page-components';
import LCPPrefetch, { getPrefetchImageArray } from '../../../../shared/components/lcp-prefetch';
import ImageModal from './image-modal';
import AwardBadges from './badges-with-award';
import { generateVideoThumbnail } from './util';

import './pdp-gallery.less';

const GalleryEnhanced = loadable(() => Helpers.retryFunc(() => import('./gallery-enhanced')));

const mapImages = (images, selectedVariant, mounted, productInitial, videoThumbnails) => {
    let { fullImages, zoomImages: xlImages, zoomedFullImages } = images;
    let defaultImages = fullImages || [];
    let zoomImages = xlImages || [];
    if (selectedVariant) {
        const { value } = selectedVariant;
        //TODO: If more than one color and there is no match, might need to default to the first color seen
        const filterByColor = ({ color }) => !color || getColorId(color) === getColorId(value);
        defaultImages = (fullImages || []).filter(filterByColor);
        zoomImages = (xlImages || []).filter(filterByColor);
        zoomedFullImages = (zoomedFullImages || []).filter(filterByColor);
    }

    const videoUrls = [...defaultImages, ...(zoomedFullImages || []), ...zoomImages]
        .filter(img => img && img.type === 'video' && img.url)
        .map(img => img.url);
    const zoomedImages = zoomedFullImages?.length ? zoomedFullImages : defaultImages;

    return {
        defaultImages: setVideoDefaultImages(defaultImages, mounted, productInitial, videoThumbnails),
        zoomImages,
        zoomedFullImages: setVideoDefaultImages(zoomedImages, true, productInitial, videoThumbnails),
        videoUrls,
    };
};

/**
 * Adds alt text and video thumbnails for gallery assets.
 * Returns only the first image for SSR, since lazy load doesn't work well with SSR
 * @param {*} images
 * @param {*} mounted
 * @returns
 */
const setVideoDefaultImages = (images, mounted, productInitial, videoThumbnails = {}) => {
    if (!images || images.length === 0) return [];

    const alt = `${productInitial.pm_category || productInitial.pm_subcategory}, ${productInitial.name}`;
    const setImageAlt = (img, count) => {
        return { ...img, alt: `Series ${count} of ${images.length} ${alt}` };
    };
    // Find first still image and use it as a fallback thumbnail for non-image assets.
    const imageIndex = images.findIndex(t => t.type !== 'video' && t.type !== '360');
    const firstImage = { ...(images[imageIndex] || images[0] || {}), alt };
    if (!mounted) {
        return [firstImage, ...images.slice(1).map((i, idx) => ({ url: '' }))];
    }
    return images.map((img, index) => {
        if (img.type === 'video') {
            // Keep thumbnails video-specific: generated thumbnail first, then provided thumbnailUrl.
            const thumbnailUrl = videoThumbnails[img.url] || img.thumbnailUrl || firstImage.url || '';
            // videoUrl keeps the real source intact so the modal's own mapImages can play it, even though url is the poster here.
            return { ...img, url: thumbnailUrl, thumbnailUrl, videoUrl: img.url, alt };
        }
        if (img.type === '360') {
            return {
                ...img,
                alt,
            };
        }
        return setImageAlt(img, index + 1);
    });
};

/**
 * Adds environmental certification badges to to any existing partner logos
 * @param {*} product
 * @param {*} pdpPartnerLogos
 * @returns
 */
const getBadges = (product, pdpPartnerLogos) => {
    const envBadges = getEnvironmentalCertificationBadges(product);
    const { logos, awards } = pdpPartnerLogos || [];
    return envBadges.length > 0 ? { logos: [...(logos || []), ...envBadges], awards } : pdpPartnerLogos || [];
};

const pdpGallery = ({
    pdpImages,
    ctoConfig,
    pdpPartnerLogos,
    productInitial,
    pdpSettings,
    ctaProps,
    enableZoomGallery = true,
}) => {
    if (pdpImages && productInitial) {
        const { productImage } = useTemplateTheme(['productImage']);
        const { classes } = productImage || {};
        const { colorCTOs, sku, facet_aipc, dte_facet_OS } = productInitial;
        const { openGalleryGtmActions, getGtmActions, has3D, hasVideo } = pdpImages || {};
        const { disableAutoThumbnail } = pdpSettings || {};
        const [imageIndex, setImageIndex] = useState(-1);
        const [mounted, setMounted] = useState(false);
        const [videoThumbnails, setVideoThumbnails] = useState({});
        //don't render gallery till interaction is made
        const [loadModal, setLoadModal] = useState(false);
        const configurations = getConfigurations(colorCTOs, ctoConfig.color);
        const selectedVariant = getSelectedVariant(configurations, ctoConfig.color);
        const mappedImages = mapImages(pdpImages, selectedVariant, mounted, productInitial, videoThumbnails);
        const { logos: badges, awards } = getBadges(productInitial, pdpPartnerLogos);
        const videoUrls = mappedImages.videoUrls;
        const videoUrlsKey = videoUrls.join('|');

        const prefetchList = (() => {
            const images = mappedImages.defaultImages;
            if (!images || images.length === 0) return [];
            const list = [];
            const first = images[0];
            if (first) list.push(first);
            const img360 = images.find(i => i && (i.type === '360' || i.type === 360));
            if (img360 && img360.url && img360.url !== first?.url) {
                list.push(img360);
            }
            return list;
        })();

        const prefetchImages = getPrefetchImageArray(`${sku}-${prefetchList.length}`, prefetchList, image => ({
            href: image?.url,
            type: image.type === '360' ? 'fetch' : 'image',
        }));

        useEffect(() => {
            if (!mounted) {
                setMounted(true);
            }
        }, [mounted]);

        useEffect(() => {
            let cancelled = false;

            if (!mounted || videoUrls.length < 1 || disableAutoThumbnail) {
                return () => {
                    cancelled = true;
                };
            }

            const uniqueVideoUrls = [...new Set(videoUrls)];
            const relevantVideoUrls = new Set(uniqueVideoUrls);

            // Drop cached thumbnails for videos no longer relevant to the current variant selection.
            setVideoThumbnails(prevVideoThumbnails => {
                const cachedVideoUrls = Object.keys(prevVideoThumbnails);
                const hasStaleVideoThumbnails = cachedVideoUrls.some(url => !relevantVideoUrls.has(url));
                if (!hasStaleVideoThumbnails) {
                    return prevVideoThumbnails;
                }
                const filteredVideoThumbnails = {};
                cachedVideoUrls.forEach(url => {
                    if (relevantVideoUrls.has(url)) {
                        filteredVideoThumbnails[url] = prevVideoThumbnails[url];
                    }
                });
                return filteredVideoThumbnails;
            });

            Promise.all(
                uniqueVideoUrls.map(async url => {
                    const thumbnail = await generateVideoThumbnail(url);
                    return [url, thumbnail];
                }),
            ).then(results => {
                if (cancelled) return;

                setVideoThumbnails(prevVideoThumbnails => {
                    const nextVideoThumbnails = { ...prevVideoThumbnails };
                    results.forEach(([url, thumbnail]) => {
                        if (thumbnail) {
                            nextVideoThumbnails[url] = thumbnail;
                        }
                    });
                    return nextVideoThumbnails;
                });
            });

            return () => {
                cancelled = true;
            };
        }, [mounted, videoUrlsKey, disableAutoThumbnail]);

        if (!mappedImages.defaultImages || mappedImages.defaultImages.length < 1) {
            return null;
        }

        const onExpand = index => {
            if (enableZoomGallery) {
                try {
                    if (!loadModal) {
                        setLoadModal(true);
                    }
                    setImageIndex(index);
                } catch (e) {}
            }
        };

        //TODO: Temp solution would be better to have Gallery component take lazyLoad as a prop
        return (
            <>
                {awards && <AwardBadges className="award-logos" badges={awards} />}
                <LCPPrefetch images={prefetchImages} />

                {has3D ? (
                    <GalleryEnhanced
                        images={mappedImages.defaultImages}
                        badges={badges}
                        onExpand={onExpand}
                        hasVideo={hasVideo}
                    />
                ) : (
                    <Gallery
                        /** set key to cto color so switch color options with different amount of images doesn't break*/
                        key={ctoConfig.color || 'default'}
                        onExpand={index => setTimeout(() => onExpand(index), 0)}
                        className={`pdp-gallery${!mounted ? ' ssr' : ''}${awards?.length > 0 ? ' badges-awards' : ''}`}
                        images={mounted ? mappedImages.defaultImages : mappedImages.defaultImages.slice(0, 1)}
                        lazyLoad={mounted ? 'ondemand' : null}
                        gtmActions={openGalleryGtmActions}
                        badges={badges}
                        badgesPosition="bottom-center"
                        controls="thumbnails"
                        thumbnailsOrientation="vertical"
                        badgesCustomSize
                        classes={{
                            ...(classes || {}),
                            badge: (classes && classes.badge ? classes.badge + ' ' : '') + 'gallery-badges',
                        }}
                    />
                )}
                {!has3D && badges && badges.length > 0 && (
                    <div className="badge-divider" aria-hidden="true" role="presentation" />
                )}
                {loadModal && (
                    <ImageModal
                        imageIndex={imageIndex}
                        setImageIndex={setImageIndex}
                        images={mappedImages}
                        ctoConfig={ctoConfig}
                        getGtmActions={getGtmActions}
                        ctaProps={ctaProps}
                        has3D={has3D}
                    />
                )}
            </>
        );
    }
    return null;
};

export default withPageComponents(withConfigurations(pdpGallery), {
    components: ['pdpImages', 'productInitial', 'pdpPartnerLogos', 'pdpSettings'],
});
